import { ArrowRight } from 'lucide-react';

/**
 * Success Stories Section
 * Design: Navy background with gold accents, 3 case studies in cards
 * Each card shows: Industry, Before/After metrics, Client quote, CTA
 */

const CASE_STUDIES = [
  {
    id: 1,
    industry: 'Retail & E-commerce',
    clientName: 'Sarah M.',
    businessName: 'The Boutique Co.',
    challenge: 'Spending 35 hours/month on bookkeeping with spreadsheet chaos',
    results: [
      { metric: '10 hrs', change: '/month saved', label: 'Time freed up' },
      { metric: '$1,200', change: '/month value', label: 'Time value' },
      { metric: '$901', change: '/month net', label: 'Your savings' },
    ],
    quote: '"I went from drowning in receipts to having 10 extra hours every month to focus on growing my business. The ROI was immediate."',
    image: '👩‍💼',
  },
  {
    id: 2,
    industry: 'Hospitality & Cafés',
    clientName: 'James T.',
    businessName: 'Coastal Café',
    challenge: 'Monthly reconciliation errors causing cash flow confusion',
    results: [
      { metric: '20 hrs', change: '/month saved', label: 'Time freed up' },
      { metric: '$2,400', change: '/month value', label: 'Time value' },
      { metric: '$2,201', change: '/month net', label: 'Your savings' },
    ],
    quote: '"With 20 hours saved monthly, I can finally focus on customer experience instead of spreadsheets. My business has grown 30% since."',
    image: '☕',
  },
  {
    id: 3,
    industry: 'Professional Services',
    clientName: 'Rebecca L.',
    businessName: 'Health & Wellness Studio',
    challenge: 'Xero setup incomplete, tax preparation was stressful',
    results: [
      { metric: '35 hrs', change: '/month saved', label: 'Time freed up' },
      { metric: '$4,200', change: '/month value', label: 'Time value' },
      { metric: '$3,801', change: '/month net', label: 'Your savings' },
    ],
    quote: '"Saving 35 hours per month means I\'m not working weekends anymore. The financial clarity and time freedom have been transformational."',
    image: '🧘‍♀️',
  },
];

export default function SuccessStoriesSection() {
  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-background to-slate-50">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-sm font-600 tracking-widest text-primary mb-3">
            REAL RESULTS
          </p>
          <h2 className="text-4xl lg:text-5xl font-serif font-700 text-foreground mb-4">
            Perth Businesses That
            <span className="block text-primary italic font-400">
              Transformed Their Books
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See how real business owners went from financial chaos to complete clarity and freed up hours every week.
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {CASE_STUDIES.map((caseStudy) => (
            <div
              key={caseStudy.id}
              className="bg-white rounded-lg border border-border shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden"
            >
              {/* Card Header with Industry */}
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-6 py-4 border-b border-border">
                <p className="text-xs font-600 tracking-widest text-primary mb-1">
                  {caseStudy.industry}
                </p>
                <p className="text-sm font-600 text-foreground">
                  {caseStudy.businessName}
                </p>
              </div>

              {/* Card Body */}
              <div className="p-6">
                {/* Challenge */}
                <div className="mb-6">
                  <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">
                    The Challenge
                  </p>
                  <p className="text-sm text-foreground leading-relaxed">
                    {caseStudy.challenge}
                  </p>
                </div>

                {/* Results Grid */}
                <div className="grid grid-cols-3 gap-3 mb-6 pb-6 border-b border-border">
                  {caseStudy.results.map((result, idx) => (
                    <div key={idx} className="text-center">
                      <p className="text-xl font-700 text-primary mb-1">
                        {result.metric}
                      </p>
                      <p className="text-xs text-primary/70 font-600 mb-1">
                        {result.change}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {result.label}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Quote */}
                <div className="mb-6">
                  <p className="text-sm italic text-foreground leading-relaxed mb-3">
                    {caseStudy.quote}
                  </p>
                  <p className="text-xs font-600 text-muted-foreground">
                    — {caseStudy.clientName}
                  </p>
                </div>

                {/* CTA */}
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 text-sm font-600 text-primary hover:text-primary/80 transition-colors"
                >
                  Get Similar Results <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center bg-primary/5 rounded-lg border border-primary/20 px-8 py-10">
          <h3 className="text-2xl font-serif font-700 text-foreground mb-3">
            Ready to Transform Your Books?
          </h3>
          <p className="text-foreground/70 mb-6 max-w-2xl mx-auto">
            These results are typical, not exceptional. Most clients see significant time and cost savings within the first month.
          </p>
          <a
            href="#contact"
            className="inline-block px-8 py-3 bg-primary text-primary-foreground font-600 rounded-md hover:bg-primary/90 transition-colors"
          >
            Book Your Free Consultation
          </a>
        </div>
      </div>
    </section>
  );
}
